import { useCallback, useEffect, useRef, useState } from 'react';
import type { NextRouter } from 'next/router';

export const CHAT_CHANNELS = ['', 'WHATSAPP', 'SMS', 'EXTERNAL_AGENT', 'CALENDAR_MEETINGS'] as const;
export const MEETINGS_CHAT_CHANNEL = 'CALENDAR_MEETINGS';
/** The meetings tab exists only inside the `symphonyMeetingsAgent` release. */
export function visibleChatChannels(meetingsEnabled: boolean): readonly string[] {
  return meetingsEnabled ? CHAT_CHANNELS : CHAT_CHANNELS.filter((channel) => channel !== MEETINGS_CHAT_CHANNEL);
}
export const stringParam = (value: string | string[] | undefined) => typeof value === 'string' ? value : '';
/** A link or restored URL naming a tab the reader cannot see lands on All, never on a hidden tab. */
export function chatsFilters(query: NextRouter['query'], meetingsEnabled = false) {
  const value = stringParam(query.channel);
  return { search: stringParam(query.q).slice(0, 200), channel: visibleChatChannels(meetingsEnabled).includes(value) ? value : '' };
}
const key = (filters: { search: string; channel: string }) => JSON.stringify(filters);

/** Filters live in the URL so hydration, browser Back and a shared link restore the same list. */
export function useChatsFilters(router: NextRouter, basePath: string, meetingsEnabled = false) {
  const [filters, setFilters] = useState(() => chatsFilters(router.query, meetingsEnabled));
  const pending = useRef<string | null>(null);
  useEffect(() => {
    // A browser history navigation supersedes an outstanding shallow replace from typing.
    const pop = () => { pending.current = null; };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);
  const fromUrl = key(chatsFilters(router.query, meetingsEnabled));
  useEffect(() => {
    if (!router.isReady || (pending.current && pending.current !== fromUrl)) return;
    pending.current = null;
    setFilters(JSON.parse(fromUrl) as typeof filters);
  }, [router.isReady, fromUrl]);
  const update = useCallback((next: typeof filters) => {
    setFilters(next);
    pending.current = key(next);
    const { q: _q, channel: _channel, ...rest } = router.query;
    const query = { ...rest, ...(next.search ? { q: next.search } : {}), ...(next.channel ? { channel: next.channel } : {}) };
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) if (typeof value === 'string') params.set(name, value);
    void router.replace({ pathname: '/chats', query }, `${basePath}/chats${params.size ? `?${params}` : ''}`, { shallow: true, scroll: false })
      .catch(() => { pending.current = null; });
  }, [router, basePath]);
  return { ...filters, setSearch: (search: string) => update({ ...filters, search }), setChannel: (channel: string) => update({ ...filters, channel }) };
}
